import { cloneElement, isValidElement } from 'react';
import PropTypes from 'prop-types';

import DashboardButton from '../DashboardButton';

/**
 * Modal footer: error + Cancel + primary action.
 *
 * The same footer the widgets domain carries, which took it from
 * `aims-os-governance/src/domains/truth-plane/components/Modals/parts/ModalFooter.jsx`
 * — duplicated per domain on purpose, the way this repo says to. Cancel is a
 * `DashboardButton` here, because this domain sizes its labels at `text-xs`.
 *
 * It handles the `submitting` / `submitError` / `submitDisabled` contract and
 * clones the primary button with `loading` / `disabled`, so no dialog here
 * hand-rolls a footer — and none loses an error the way the archive and
 * duplicate confirms did, which had nowhere to show one.
 * @param {object} props
 * @param {string} props.cancelLabel
 * @param {() => void} props.onCancel
 * @param {import('react').ReactNode} props.primaryAction — typically `<Button …>`
 * @param {boolean} [props.submitting]
 * @param {string} [props.submitError]
 * @param {import('react').ReactElement} [props.secondaryAction] — **un solo botón**
 *   (se clona con `disabled` mientras `submitting`; un Fragment o un wrapper no
 *   lo recibiría). Botón extra a la
 *   izquierda del primario. No se clona con `loading`: solo el primario
 *   muestra el spinner. Sí queda `disabled` mientras `submitting`.
 *
 * While `submitting`, **every** action in the footer is disabled, not only the
 * primary: Cancel and the secondary one used to stay clickable mid-request —
 * they looked available and did nothing, or closed the dialog under a save
 * still running (CT-5504). One rule here covers every dialog that uses it.
 * @param {boolean} [props.submitDisabled]
 * @param {string} [props.statusText] — neutral note on the left (e.g. "Select an
 *   entity to continue"). Shown only when there is no `submitError`: an
 *   informational state must not inherit an error's red or its `role="alert"`.
 * @param {string} [props.className] — e.g. padding
 */
const ModalFooter = ({
  cancelLabel,
  onCancel,
  primaryAction,
  secondaryAction,
  submitting = false,
  submitError,
  submitDisabled = false,
  statusText,
  className = 'px-6 py-4',
}) => {
  const primary =
    isValidElement(primaryAction) && (submitting || submitDisabled)
      ? cloneElement(primaryAction, {
          loading: submitting || primaryAction.props?.loading,
          disabled: submitDisabled || submitting || primaryAction.props?.disabled,
        })
      : primaryAction;

  const secondary =
    isValidElement(secondaryAction) && submitting
      ? cloneElement(secondaryAction, { disabled: true })
      : secondaryAction;

  return (
    <div className={['shrink-0 border-t border-edge', className].filter(Boolean).join(' ')}>
      <div className="flex w-full items-center gap-2.5">
        {submitError ? (
          <p className="min-w-0 flex-1 pr-2 text-left text-[11px] leading-snug text-red-400" role="alert">
            {submitError}
          </p>
        ) : statusText ? (
          <p className="min-w-0 flex-1 pr-2 text-left text-[11px] leading-snug text-content-muted">
            {statusText}
          </p>
        ) : (
          <div className="flex-1" aria-hidden />
        )}
        <DashboardButton type="button" variant="secondary" onClick={onCancel} disabled={submitting}>
          {cancelLabel}
        </DashboardButton>
        {secondary}
        {primary}
      </div>
    </div>
  );
};

ModalFooter.propTypes = {
  cancelLabel: PropTypes.string,
  onCancel: PropTypes.func,
  primaryAction: PropTypes.node,
  secondaryAction: PropTypes.element,
  submitting: PropTypes.bool,
  submitError: PropTypes.string,
  submitDisabled: PropTypes.bool,
  statusText: PropTypes.string,
  className: PropTypes.string,
};

export default ModalFooter;
